import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    styled,
    Avatar,
    Container,
    CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Card } from "../../components/Card";
import useAuth from "../../context";
import API_ENDPOINTS from "../../apis/endpoints";
import { useNavigate, useParams } from "react-router-dom";
import ImageMessage from "./ImageMessage";

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(5),
    height: theme.spacing(5),
    border: `4px solid ${theme.palette.graph.cyan}`,
    boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.2)",
    backgroundColor: theme.palette.graph.cyan,
    marginTop: "-15px",
}));

const AiAvatarChat = ({ onBack }) => {
    const { modelId } = useParams();
    const storedDeviceId = localStorage.getItem("device-id");
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [oldestLoadedId, setOldestLoadedId] = useState(null);
    const [modelName, setModelName] = useState("");
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const scrollRef = useRef(null);
    const auth = useAuth();
    const navigate = useNavigate();

    const fetchChatMessages = useCallback(
        async (before = null) => {
            setLoading(true);
            try {
                const url = `${API_ENDPOINTS.AI_MODEL_AVATAR}${modelId}/chat/?limit=5${before ? `&before=${before}` : ""}`;
                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                        "ngrok-skip-browser-warning": "true",
                    },
                });
                if (!response.ok)
                    throw new Error("Failed to fetch chat messages");
                const data = await response.json();
                setModelName(data.data.model.name);
                if (data.data.messages.length < 5) {
                    setHasMoreMessages(false);
                }
                return data.data.messages.reverse();
            } catch (error) {
                console.error("Error fetching chat messages:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to fetch messages. Please try again.",
                });
                return [];
            } finally {
                setLoading(false);
            }
        },
        [modelId, auth.accessToken, storedDeviceId]
    );

    const loadMoreMessages = useCallback(async () => {
        if (loading || !oldestLoadedId || !hasMoreMessages) return;
        const newMessages = await fetchChatMessages(oldestLoadedId);
        if (newMessages.length > 0) {
            setMessages((prev) => [...prev, ...newMessages]);
            setOldestLoadedId(newMessages[newMessages.length - 1].this_id);
        }
    }, [loading, oldestLoadedId, fetchChatMessages, hasMoreMessages]);

    useEffect(() => {
        const loadInitialMessages = async () => {
            const initialMessages = await fetchChatMessages();
            setMessages(initialMessages);
            if (initialMessages.length > 0) {
                setOldestLoadedId(
                    initialMessages[initialMessages.length - 1].this_id
                );
            }
        };
        loadInitialMessages();
    }, [fetchChatMessages]);

    useEffect(() => {
        if (!scrollRef.current) return;

        const handleScroll = (e) => {
            if (
                e.target.scrollTop * -1 + e.target.clientHeight >=
                e.target.scrollHeight - 10
            ) {
                console.log("Load more messages triggered");
                loadMoreMessages();
            }
        };

        scrollRef.current.addEventListener("scroll", handleScroll);

        return () => {
            if (scrollRef.current) {
                scrollRef.current.removeEventListener("scroll", handleScroll);
            }
        };
    }, [loadMoreMessages]);

    const handleSendMessage = async () => {
        if (newMessage.trim()) {
            try {
                setLoading(true);
                const response = await fetch(
                    `${API_ENDPOINTS.AI_MODEL_AVATAR}${modelId}/chat/`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${auth.accessToken}`,
                            "x-cc-device-id": storedDeviceId,
                            "Content-Type": "application/json",
                            "ngrok-skip-browser-warning": "true",
                        },
                        body: JSON.stringify({ message: newMessage }),
                    }
                );
                if (!response.ok)
                    throw new Error(
                        `Failed to send message: ${response.statusText}`
                    );

                const data = await response.json();
                setMessages((prev) => [data.data, ...prev]);
                setNewMessage("");

                // Poll for image generation status
                const pollStatus = async () => {
                    try {
                        const statusResponse = await fetch(
                            `${API_ENDPOINTS.AI_MODEL_AVATAR}${modelId}/chat/${data.data.id}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${auth.accessToken}`,
                                    "x-cc-device-id": storedDeviceId,
                                    "ngrok-skip-browser-warning": "true",
                                },
                            }
                        );

                        if (!statusResponse.ok)
                            throw new Error(
                                `Failed to fetch status: ${statusResponse.statusText}`
                            );

                        const statusData = await statusResponse.json();
                        if (statusData.data.status === "READY") {
                            setMessages((prev) =>
                                prev.map((msg) =>
                                    msg.id === data.data.id
                                        ? statusData.data
                                        : msg
                                )
                            );
                        } else if (
                            ["PENDING", "PROCESSING"].includes(
                                statusData.data.status
                            )
                        ) {
                            setTimeout(pollStatus, 1000);
                        }
                    } catch (error) {
                        console.error("Error polling status:", error);
                    }
                };

                pollStatus();
            } catch (error) {
                console.error("Error sending message:", error);
                setSnackbar({
                    open: true,
                    message: "Failed to send message. Please try again.",
                });
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Container maxWidth="lg">
            <div
                style={{
                    display: "flex",
                    alignContent: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    gap: "1rem",
                }}
            >
                <ArrowBackIosNewRoundedIcon
                    onClick={() => navigate("list")}
                    sx={{ left: "10px" }}
                />
                <Typography variant="featureAccent">{modelName}</Typography>
            </div>

            <Card
                sx={{
                    width: "100vw",
                    maxHeight: "100vh",
                    margin: "auto",
                    marginTop: "50px",
                }}
            >
                <Box
                    sx={{
                        height: "85vh",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: 1,
                            overflowY: "scroll",
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        {[...messages].map((message) => (
                            <Box
                                key={message.id}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: message.response
                                        ? "flex-start"
                                        : "flex-end",
                                    p: 1,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        alignContent: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: "50vw",
                                            bgcolor:
                                                message.status === "ERROR"
                                                    ? "#ffcccb"
                                                    : message.response
                                                      ? "#f1f0f0"
                                                      : "#007bff",
                                            color:
                                                message.status === "ERROR"
                                                    ? "red"
                                                    : message.response
                                                      ? "black"
                                                      : "white",
                                            borderRadius: 2,
                                            p: 1,
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {message.response
                                                ? ` ${message.response}`
                                                : ` ${message.prompt}`}
                                        </Typography>
                                        {(message.status === "PENDING" ||
                                            message.status !== "READY") && (
                                            <Typography variant="caption">
                                                Processing...
                                            </Typography>
                                        )}
                                        {message.status === "ERROR" && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                Error: {message.error}
                                            </Typography>
                                        )}
                                    </Box>
                                </div>
                                {message.attachments &&
                                    message.attachments.length > 0 && (
                                        <ImageMessage
                                            url={message.attachments[0].url}
                                            alt={message.attachments[0].name}
                                        />
                                    )}
                            </Box>
                        ))}
                        {loading && (
                            <CircularProgress
                                style={{
                                    // display: "flex",
                                    alignSelf: "center",
                                }}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: "flex", p: 2, gap: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") handleSendMessage();
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<SendIcon />}
                            onClick={handleSendMessage}
                            disabled={loading}
                        >
                            Send
                        </Button>
                    </Box>
                </Box>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ open: false, message: "" })}
            >
                <Alert
                    onClose={() => setSnackbar({ open: false, message: "" })}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AiAvatarChat;
